import React, {
  FC,
  useState,
  useEffect,
  FormEvent,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentMessage,
  AgentRequestBody,
  AgentMessageResponse,
  MessageContent,
  MessageContentBlock,
  MiniThreadMatch,
  PaginationMetadata,
  WebRTCCallRequestedEvent,
} from '@dreamai/types';

import { groupToolMessages, ToolGroup } from '../../../utils/toolFormatting';
import api from '../../../api';
import { MessageSendErrorType } from './errors/conversationErrors';
import './thread-view.scss';
import { ReactComponent as ChatSendIcon } from '../../../icons/chat-send-icon.svg';
import { ReactComponent as LoadingRingsIcon } from '../../../icons/loading-rings.svg';
import { DirectFileUploader, FileUploadState } from './DirectFileUploader';
import { FilePreview } from './FilePreview';
import { FileUploadDropZone } from './FileUploadDropZone';
import { useFileUpload } from './useFileUpload';
import {
  generateToolMessageColors,
  generateColorVariants,
} from './threadUtils';
import MessageReplyIndicator from './MessageReplyIndicator';
import ChatToolGroupDisplay from './ChatToolGroupDisplay';
import MultiModalContent from './MultiModalContent';
import StreamHandler from './StreamHandler';
import ThirdPartyMessageHandler from './ThirdPartyMessageHandler';
import MessageOuterWrapper from './MessageOuterWrapper';
import AsyncToolDisplay from './AsyncToolDisplay';
import InlineResponseArtifact from '../InlineResponseArtifact';

// ConfirmationNotifications removed - confirmations are now handled via artifacts in the To-Do List
import { usePusher } from '../../../contexts/PusherContext';
import { useNewThread } from '../../../contexts/NewThreadContext';
import {
  useThreadActions,
  useThreadState,
  NEW_THREAD,
} from '../../../contexts/ThreadLoadingContext';
import { useFormCapture } from '../../../contexts/FormCaptureContext';
import { AxiosResponse } from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { useChatCanvas } from '../../../contexts/ChatCanvasContext';
import { useAgent } from '../../../contexts/AgentContext';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { useThreadReads } from '../../../providers';
import { ReactComponent as DownArrowIcon } from '../../../icons/down-arrow.svg';
import { ReactComponent as StartVoiceChatIcon } from '../../../icons/start-voice-chat.svg';
import { ReactComponent as MicIcon } from '../../../icons/mic.svg';
import { ReactComponent as CallingIcon } from '../../../icons/webrtc-calls/calling.svg';
import WebRTCVoiceChat from '../../WebRTCVoiceChat';
import MentionTextarea, { MentionedUser } from './MentionTextarea';
import MentionAccessModal from './MentionAccessModal';
import SpendingApprovalModal from './SpendingApprovalModal';
import ReplyToPreview from './ReplyToPreview';
import { useReplyToActions } from '../../../contexts/ReplyToContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { useThreadCallOptional } from '../../../contexts/ThreadCallContext';

/**
 * Returns the leading text of a message's content, regardless of whether it's
 * a plain string or an array of multimodal content blocks. We need this for
 * the system-message visibility filter: a SYSTEM_MESSAGE may be attached to
 * an image or a file, in which case `content` is the multimodal array and
 * the prefix lives on the first text block. Returns '' when no text is
 * present (e.g. an image-only message).
 */
const getLeadingMessageText = (content: AgentMessage['content']): string => {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    const textBlock = content.find(
      (b: MessageContentBlock) => b.type === 'text',
    ) as { type: 'text'; text?: string } | undefined;
    return textBlock?.text ?? '';
  }
  return '';
};

/**
 * True if a message is one of the internal system flavours we hide from the
 * user (SYSTEM_MESSAGE, SYSTEM_MANAGER, FRONTEND_HIDE_MESSAGE) — handles both
 * string content and multimodal array content (the array case is what makes
 * system messages with attachments slip through if you only check strings).
 */
const isInternalSystemMessage = (content: AgentMessage['content']): boolean => {
  const text = getLeadingMessageText(content);
  return (
    text.startsWith('SYSTEM_MESSAGE') ||
    text.startsWith('SYSTEM_MANAGER') ||
    text.startsWith('FRONTEND_HIDE_MESSAGE')
  );
};

interface ThreadViewProps {
  messages: AgentMessage[];
  threadId?: string | null;
  agentId?: string;
  agentName?: string;
  agentNickname?: string;
  predominantColour?: string;
  onMessageSent: (threadId: string, responseMessages?: AgentMessage[]) => void;
  onCreateError: (
    errorType: MessageSendErrorType,
    errorMessage: string,
    failedMessage?: string,
  ) => void;
  recoveryMessage?: string;
  onRecoveryMessageUsed?: () => void;
  setMessages: (
    messages:
      | AgentMessage[]
      | ((prevMessages: AgentMessage[]) => AgentMessage[]),
  ) => void;
  fetchThreadMessages?: (threadId: string) => Promise<void>;
  loadMoreMessages?: (
    threadId: string,
    offset: number,
    limit?: number,
  ) => Promise<PaginationMetadata | undefined>;
  paginationState?: {
    hasMore: boolean;
    total: number;
    offset: number;
    loading: boolean;
  };
  inMiniMode?: boolean;
  discussingObject?: string;
  discussingObjectMatch?: MiniThreadMatch;
  isAgentLedProject?: boolean;
  hasWritePermission?: boolean;
  isArchivedThread?: boolean;
  isSubThread?: boolean;
  onOpenCanvas?: () => void;
  readOnly?: boolean;
  obscured?: boolean;
  cipherSalt?: string | null;
  initialSubThreads?: Array<{
    id: string;
    description: string | null;
    created_at: string;
    last_updated_at: string;
    fork_message_timestamp?: string;
    fork_message_content?: string | null;
  }>;
}

/**
 * Extracts image and file URLs from uploaded files
 * @param files - Array of uploaded files
 * @returns Object containing arrays of image and file URLs
 */
const extractFileUrls = (
  files: FileUploadState[],
): { imageUrls: string[]; fileUrls: string[] } => {
  const imageUrls: string[] = [];
  const fileUrls: string[] = [];

  files.forEach((file) => {
    if (file.url) {
      if (file.type.startsWith('image/')) {
        imageUrls.push(file.url);
      } else {
        fileUrls.push(file.url);
      }
    }
  });

  return { imageUrls, fileUrls };
};

/**
 * Formats a timestamp into a readable date string
 * Uses "Today", "Yesterday", or "Day, DD Month" format
 */
const formatDateDivider = (timestamp: number): string => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Reset time to midnight for comparison
  const resetTime = (d: Date) => {
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const dateNoTime = resetTime(new Date(date));
  const todayNoTime = resetTime(new Date(today));
  const yesterdayNoTime = resetTime(new Date(yesterday));

  if (dateNoTime.getTime() === todayNoTime.getTime()) {
    return 'Today';
  } else if (dateNoTime.getTime() === yesterdayNoTime.getTime()) {
    return 'Yesterday';
  } else {
    // Format as "Monday, 14 November"
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    };
    return date.toLocaleDateString('en-GB', options);
  }
};

/**
 * Checks if two timestamps are on different days
 */
const isDifferentDay = (timestamp1: number, timestamp2: number): boolean => {
  const date1 = new Date(timestamp1);
  const date2 = new Date(timestamp2);

  return (
    date1.getDate() !== date2.getDate() ||
    date1.getMonth() !== date2.getMonth() ||
    date1.getFullYear() !== date2.getFullYear()
  );
};

/**
 * Formats a timestamp into a readable time string
 * Returns format like "3:45 PM"
 */
const formatMessageTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Creates a message content structure for the agent message
 * @param message - The text message content
 * @param uploadedFiles - Array of successfully uploaded files
 * @returns String message or array of content items for multi-modal messages
 */
const createMessageContent = (
  message: string,
  uploadedFiles: FileUploadState[],
): string | Array<{ type: string; [key: string]: any }> => {
  // If no uploaded files, just return the message text
  if (!uploadedFiles || uploadedFiles.length === 0) {
    return message;
  }

  // Initialize as array with text content
  const contentArray: Array<{ type: string; [key: string]: any }> = [
    { type: 'text', text: message },
  ];

  // Add images and files to the content array
  uploadedFiles.forEach((file) => {
    if (file.url) {
      if (file.type.startsWith('image/')) {
        // Add image content
        contentArray.push({
          type: 'image_url',
          image_url: {
            url: file.url,
          },
        });
      } else {
        // Add file content
        const filename = file.name || 'File';
        contentArray.push({
          type: 'file',
          url: file.url,
          mime_type: file.type,
          metadata: {
            filename: filename,
          },
        });
      }
    }
  });

  return contentArray;
};

const ThreadView: FC<ThreadViewProps> = ({
  messages,
  threadId,
  agentId,
  agentName,
  agentNickname,
  predominantColour,
  onMessageSent,
  onCreateError,
  recoveryMessage,
  onRecoveryMessageUsed,
  setMessages,
  fetchThreadMessages,
  loadMoreMessages,
  paginationState = {
    hasMore: false,
    total: 0,
    offset: 0,
    loading: false,
  },
  inMiniMode = false,
  discussingObject,
  discussingObjectMatch,
  isAgentLedProject = false,
  hasWritePermission = true,
  isArchivedThread = false,
  isSubThread = false,
  onOpenCanvas,
  readOnly = false,
  obscured = false,
  cipherSalt = null,
  initialSubThreads = [],
}) => {
  const { user } = useAuth0();
  const currentUserId = user?.sub;
  const [newMessage, setNewMessage] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadState[]>([]);
  const [isAudioMode, setIsAudioMode] = useState(false);
  const [shouldHangupCall, setShouldHangupCall] = useState(false);
  const [isAudioConnected, setIsAudioConnected] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [showCallPrompt, setShowCallPrompt] = useState(false);
  const [callPromptMessage, setCallPromptMessage] = useState<string | null>(
    null,
  );
  const [callPromptAgentName, setCallPromptAgentName] = useState<string | null>(
    null,
  );
  const [callPromptStructuredCallId, setCallPromptStructuredCallId] = useState<
    string | null
  >(null);
  const [outputLevel, setOutputLevel] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  // confirmationsExpanded removed - confirmations now handled via artifacts
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const scrollLockedRef = useRef(false);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [mentions, setMentions] = useState<MentionedUser[]>([]);
  const [showMentionAccessModal, setShowMentionAccessModal] = useState(false);
  const [usersNeedingAccess, setUsersNeedingAccess] = useState<MentionedUser[]>(
    [],
  );
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [pendingMentions, setPendingMentions] = useState<MentionedUser[]>([]);
  const [showSpendingApprovalModal, setShowSpendingApprovalModal] =
    useState(false);
  const [pendingSpendMessage, setPendingSpendMessage] = useState<string | null>(
    null,
  );
  const [pendingSpendMentions, setPendingSpendMentions] = useState<
    MentionedUser[]
  >([]);
  const [checkingSpendingIntent, setCheckingSpendingIntent] = useState(false);
  const [decipheredMessages, setDecipheredMessages] = useState<Set<number>>(
    new Set(),
  );
  const [decipheringMessage, setDecipheringMessage] = useState<number | null>(
    null,
  );
  const [isAgentThinking, setIsAgentThinking] = useState(false);
  const { newThreadId, setNewThreadId } = useNewThread();
  const { loadingThreads, abortingThreads } = useThreadState();
  const {
    setThreadLoading,
    setThreadAborting,
    registerAgentErrorCallback,
    unregisterAgentErrorCallback,
    registerWebRTCCallRequestedCallback,
    unregisterWebRTCCallRequestedCallback,
    dismissWebRTCCall,
    acceptWebRTCCall,
    getActiveWebRTCCall,
    clearActiveWebRTCCall,
  } = useThreadActions();
  const { getFormValues } = useFormCapture();
  const { chatCanvasRef } = useChatCanvas();
  const { agents } = useAgent();
  const { voiceCallingEnabled } = useFeatureFlags();
  // Treat the inbound call prompt as absent whenever voice calling is disabled.
  // An incoming call can still set showCallPrompt, but its dismiss UI is gated
  // off — without this the composer (send/upload/mic) would stay hidden with no
  // way to recover.
  const effectiveShowCallPrompt = showCallPrompt && voiceCallingEnabled;
  const { clearReplyTo, getReplyTo } = useReplyToActions();
  const { aiTimeMinutes, resetsAt } = useCompany();

  const { getAccessTokenSilently } = useAuth0();

  // Handle file upload state updates
  const handleFileUpload = useCallback((fileState: FileUploadState) => {
    setUploadedFiles((prevFiles) => {
      // If the file already exists in the array, update it
      const fileIndex = prevFiles.findIndex((file) => file.id === fileState.id);
      if (fileIndex !== -1) {
        const newFiles = [...prevFiles];
        newFiles[fileIndex] = fileState;
        return newFiles;
      }
      // Otherwise add it to the array
      return [...prevFiles, fileState];
    });
  }, []);

  // Get file upload cleanup function
  const { cleanupPreviews } = useFileUpload(handleFileUpload);

  // Find the agent data once for the entire thread
  const agent = agents.find((a) => a.id === agentId);

  // Credits only gate PA threads — SA/System threads are billed separately
  const isPA = (agent?.agent_type as string) === 'Personal Assistant';
  const isOutOfCredits = isPA && aiTimeMinutes <= 0;

  // Preload agent avatar image - keep decoded in memory via hidden img ref
  const avatarPreloadRef = useRef<HTMLImageElement | null>(null);
  useEffect(() => {
    if (agent?.image_url && !avatarPreloadRef.current) {
      const img = new Image();
      img.src = agent.image_url;
      avatarPreloadRef.current = img;
    }
  }, [agent?.image_url]);

  // Generate color variants once for the entire thread
  const colorVariants = predominantColour
    ? generateColorVariants(predominantColour)
    : null;

  // Get URL params - im=true shows internal messages (SYSTEM_MESSAGE, SYSTEM_MANAGER, FRONTEND_HIDE_MESSAGE)
  const [searchParams] = useSearchParams();
  const showInternalMessages = searchParams.get('im') === 'true';

  // Check if user has sent any messages (excluding system messages)
  const hasUserSentMessages = useMemo(() => {
    return messages.some((message) => {
      if (message.role !== 'user') return false;

      // Show internal messages if im=true URL param is set
      if (showInternalMessages) return true;

      // Hide if this is an internal SYSTEM_* / FRONTEND_HIDE_MESSAGE — works
      // for both plain-string content and multimodal arrays (i.e. system
      // messages that also carry an image or file attachment).
      return !isInternalSystemMessage(message.content);
    });
  }, [messages, showInternalMessages]);

  // Use newThreadId as a fallback when threadId is null
  const threadIdSafe = threadId || newThreadId || null;

  // Expose a call starter to outer chrome (chat panel header call button).
  // Registered only while a call can actually be started from this view.
  const threadCall = useThreadCallOptional();
  const registerCallStarter = threadCall?.registerCallStarter;
  useEffect(() => {
    if (!registerCallStarter) return;
    const callable =
      voiceCallingEnabled &&
      !!threadIdSafe &&
      threadIdSafe !== NEW_THREAD &&
      !readOnly &&
      !isArchivedThread &&
      hasWritePermission &&
      !isAudioMode &&
      !effectiveShowCallPrompt;
    if (callable) {
      registerCallStarter(() => setIsAudioMode(true));
    } else {
      registerCallStarter(null);
    }
    return () => registerCallStarter(null);
  }, [
    registerCallStarter,
    voiceCallingEnabled,
    threadIdSafe,
    readOnly,
    isArchivedThread,
    hasWritePermission,
    isAudioMode,
    effectiveShowCallPrompt,
  ]);

  // Check loading state for both threadId and new threads

  const loading = threadIdSafe
    ? !!loadingThreads[threadIdSafe]
    : !!loadingThreads[NEW_THREAD];
  const aborting = threadIdSafe
    ? !!abortingThreads[threadIdSafe]
    : !!abortingThreads[NEW_THREAD];

  // Use the appropriate thread ID for setting loading/aborting states
  const setLoading = (isLoading: boolean) =>
    setThreadLoading(threadIdSafe, isLoading);
  const setAborting = (isAborting: boolean) =>
    setThreadAborting(threadIdSafe, isAborting);

  const { markThreadAsRead } = useThreadReads();

  // Store the lastReadAt timestamp for showing divider
  // Only set if the userId in the marker matches current user
  // Using useState instead of useRef so setting it triggers a re-render
  const [lastReadAtForDivider, setLastReadAtForDivider] = useState<
    string | null
  >(null);

  // Decode readMarker from URL params on mount
  useEffect(() => {
    const readMarker = searchParams.get('read_marker');
    console.log('readMarker from URL:', readMarker);
    console.log('currentUserId:', currentUserId);

    // If no read marker in URL, clear the divider
    if (!readMarker) {
      setLastReadAtForDivider(null);
      return;
    }

    // If we have a marker but no userId yet, wait for auth to load
    if (!currentUserId) {
      console.log('Waiting for currentUserId to load...');
      return;
    }

    // Decode and set the divider
    try {
      // Decode: base64 -> JSON -> { userId, lastReadAt }
      const decoded = JSON.parse(atob(readMarker));
      console.log('Decoded readMarker:', decoded);

      // Only use if userId matches current user (prevents shared link issues)
      if (decoded.userId === currentUserId && decoded.lastReadAt) {
        setLastReadAtForDivider(decoded.lastReadAt);
        console.log('Set lastReadAtForDivider to:', decoded.lastReadAt);
      } else {
        console.log(
          'UserId mismatch or no lastReadAt:',
          decoded.userId,
          currentUserId,
        );
        setLastReadAtForDivider(null);
      }
    } catch (e) {
      console.error('Failed to decode readMarker:', e);
      setLastReadAtForDivider(null);
    }
  }, [threadId, searchParams, currentUserId]);

  // Debounced mark as read function to prevent excessive API calls
  const debouncedMarkAsReadRef = useRef<NodeJS.Timeout | null>(null);
  const debouncedMarkAsRead = useCallback(
    (threadId: string) => {
      if (debouncedMarkAsReadRef.current) {
        clearTimeout(debouncedMarkAsReadRef.current);
      }
      debouncedMarkAsReadRef.current = setTimeout(() => {
        markThreadAsRead(threadId);
      }, 1000); // 1 second debounce
    },
    [markThreadAsRead],
  );

  // Clean up uploaded files, preview URLs, and mentions when switching threads
  useEffect(() => {
    setUploadedFiles([]);
    setMentions([]);
    cleanupPreviews();
  }, [threadId, cleanupPreviews]);

  // Check for active WebRTC call when navigating to thread
  useEffect(() => {
    if (threadId && threadId !== NEW_THREAD) {
      const activeCall = getActiveWebRTCCall(threadId);
      if (activeCall) {
        console.log(
          'ThreadView: Found active call for thread',
          threadId,
          activeCall,
        );
        setShowCallPrompt(true);
        setCallPromptMessage(activeCall.message || null);
        setCallPromptAgentName(activeCall.agentName || null);
        setCallPromptStructuredCallId(activeCall.structuredCallId || null);
      } else {
        // Clear call prompt when switching to thread without active call
        setShowCallPrompt(false);
        setCallPromptMessage(null);
        setCallPromptAgentName(null);
        setCallPromptStructuredCallId(null);
      }
    }
  }, [threadId, getActiveWebRTCCall]);

  // Mark thread as read when viewing it (initial load)
  useEffect(() => {
    // Only mark as read if we have a valid threadId (not NEW_THREAD)
    if (threadId && threadId !== NEW_THREAD) {
      markThreadAsRead(threadId);
    }
  }, [threadId, markThreadAsRead]);

  // Mark as read when messages change (new messages arrive while viewing)
  useEffect(() => {
    if (threadId && threadId !== NEW_THREAD && messages.length > 0) {
      debouncedMarkAsRead(threadId);
    }
  }, [messages.length, threadId, debouncedMarkAsRead]);

  // Mark as read when window regains focus (user returns to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (threadId && threadId !== NEW_THREAD) {
        markThreadAsRead(threadId);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      // Cleanup debounce timeout on unmount
      if (debouncedMarkAsReadRef.current) {
        clearTimeout(debouncedMarkAsReadRef.current);
      }
    };
  }, [threadId, markThreadAsRead]);

  // Pre-fill message from URL param on first load
  useEffect(() => {
    const messageParam = searchParams.get('message');
    if (messageParam && !newMessage) {
      setNewMessage(messageParam);
    }
  }, [searchParams]);

  // Listen for external fill-chat-input events (e.g. routine suggestion bubbles, sample routine setup)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;
      if (typeof detail === 'string') {
        setNewMessage(detail);
      } else if (typeof detail === 'object' && detail.text) {
        setNewMessage(detail.text);
        if (detail.autoSend) {
          // Small delay to let state update, then submit
          setTimeout(() => {
            const form = document.querySelector(
              '.thread-view-form',
            ) as HTMLFormElement;
            form?.requestSubmit();
          }, 50);
        }
      }
    };
    window.addEventListener('fill-chat-input', handler);
    return () => window.removeEventListener('fill-chat-input', handler);
  }, []);

  // Note: ThreadLoadingContext now automatically registers threads from URL and NewThreadContext

  // No longer need optimisticLoading state as we use NEW_THREAD key
  const [threadIdForAbort, setThreadIdForAbort] = useState<string | null>(null);
  const [lastSubmittedMessage, setLastSubmittedMessage] = useState('');
  const [resetStreamHandlerErrorFlag, setResetStreamHandlerErrorFlag] =
    useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lastMessageRef = React.useRef<string>('');

  const { ensureConnection } = usePusher(true);

  // Handle deciphering an obscured message on click
  const handleDecipherMessage = useCallback(
    async (message: AgentMessage) => {
      if (!obscured || !cipherSalt || !threadId) return;
      if (decipheringMessage !== null) return;

      // Find the original message index by reference
      const originalIndex = messages.findIndex((m) => m === message);
      if (originalIndex === -1) return;
      if (decipheredMessages.has(originalIndex)) return;

      try {
        setDecipheringMessage(originalIndex);
        const token = await getAccessTokenSilently();

        const response = await api.post(
          `/agents/thread/${threadId}/decipher`,
          {
            content: message.content,
            salt: cipherSalt,
            toolCallInput: message.tool_call_input,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.data.success) {
          setMessages((prevMessages) => {
            const newMessages = [...prevMessages];
            if (newMessages[originalIndex]) {
              newMessages[originalIndex] = {
                ...newMessages[originalIndex],
                content:
                  response.data.content ?? newMessages[originalIndex].content,
                tool_call_input:
                  response.data.toolCallInput ??
                  newMessages[originalIndex].tool_call_input,
                metadata: {
                  ...newMessages[originalIndex].metadata,
                  obscured: false,
                },
              };
            }
            return newMessages;
          });
          setDecipheredMessages((prev) => {
            const newSet = new Set(prev);
            newSet.add(originalIndex);
            return newSet;
          });
        }
      } catch (error) {
        console.error('Error deciphering message:', error);
      } finally {
        setDecipheringMessage(null);
      }
    },
    [
      obscured,
      cipherSalt,
      threadId,
      messages,
      decipheredMessages,
      decipheringMessage,
      getAccessTokenSilently,
      setMessages,
    ],
  );

  // Memoize the onMessageSent callback to prevent unnecessary reconnections in StreamHandler
  const memoizedOnMessageSent = useCallback(
    (threadId: string, responseMessages?: AgentMessage[]) => {
      onMessageSent(threadId, responseMessages);
    },
    [onMessageSent],
  );

  // Track if this is the first scroll
  const isFirstScrollRef = React.useRef(true);

  // Function to scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current && !scrollLockedRef.current) {
      // Use instant scrolling for first load, smooth scrolling after that
      messagesEndRef.current.scrollIntoView({
        behavior: isFirstScrollRef.current ? 'auto' : 'smooth',
      });

      // After first scroll, set flag to false
      if (isFirstScrollRef.current) {
        isFirstScrollRef.current = false;
      }
    } else {
      console.log('Scroll blocked - scrollLockedRef:', scrollLockedRef.current);
    }
  }, [messagesEndRef]);

  // Scroll to bottom on initial thread load
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages.length, scrollToBottom]);

  // Function to handle scroll to bottom button click
  const handleScrollToBottomClick = useCallback(() => {
    scrollLockedRef.current = false;
    setNewMessagesCount(0);
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Detect when user scrolls to bottom to unlock auto-scroll
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 10; // 10px threshold

    if (isAtBottom && scrollLockedRef.current) {
      scrollLockedRef.current = false;
      setNewMessagesCount(0);
    }
  }, []);

  // Function to clean up after a send failure - memoized to prevent unnecessary reconnections
  const sendFailCleanup = useCallback(() => {
    // Remove the optimistic message
    setMessages((prevMessages: AgentMessage[]): AgentMessage[] =>
      prevMessages.slice(0, -1),
    );
    // Reset aborting state if it was set
    setAborting(false);
  }, [setMessages]);

  // Handler for when abort succeeds
  const handleAbortSucceeded = useCallback(() => {
    console.log('ThreadView: Abort succeeded');
    setAborting(false);
    setLoading(false);

    // If this is a new thread (no thread_id in URL), we need to update the URL
    // since we won't reach the AGENT_COMPLETE event
    if (threadIdForAbort && !threadId && onMessageSent) {
      console.log(
        'ThreadView: Calling onMessageSent for new aborted thread',
        threadIdForAbort,
      );
      onMessageSent(threadIdForAbort, []);
    }
  }, [threadIdForAbort, threadId, onMessageSent]);

  // Handler for agent errors
  const handleAgentError = useCallback(
    (threadId: string) => {
      console.log('ThreadView: Agent error occurred', threadId);

      // If this is a new thread (no thread_id in URL) and we have a thread ID in the data,
      // we need to update the URL since we won't reach the AGENT_COMPLETE event
      if (
        !searchParams.get('thread_id') &&
        threadId === threadIdForAbort &&
        threadIdForAbort &&
        onMessageSent
      ) {
        console.log('ThreadView: Updating URL with error thread ID', threadId);
        onMessageSent(threadIdForAbort, []);
      }
    },
    [searchParams, onMessageSent, threadIdForAbort],
  );

  // Function to abort the streaming response
  const handleAbortStreaming = useCallback(async () => {
    // Use threadIdSafe if available, otherwise fall back to threadIdForAbort
    const abortThreadId = threadIdSafe || threadIdForAbort;
    if (!abortThreadId) return;

    try {
      setAborting(true);

      const token = await getAccessTokenSilently();
      await api.post(
        `/agents/thread/${abortThreadId}/abort`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // If this is a new thread (no thread_id in URL), we need to update the URL
      // since we won't reach the AGENT_COMPLETE event

      if (!searchParams.get('thread_id') && onMessageSent) {
        console.log(
          'ThreadView: Updating URL with aborted thread ID',
          abortThreadId,
        );
        onMessageSent(abortThreadId, []);
      }

      // We don't immediately clear the loading state here
      // It will be cleared when we receive the agent-complete event via Pusher
    } catch (error) {
      console.error('Error aborting streaming:', error);
      // If abort fails, clear aborting state
      setAborting(false);
    }
  }, [
    threadIdSafe,
    threadIdForAbort,
    onMessageSent,
    getAccessTokenSilently,
    setAborting,
    setLoading,
  ]);

  // Function to recover the message text when processing fails
  const recoverFromProcessingError = useCallback(() => {
    // First try to use the ref (synchronous update)
    if (lastMessageRef.current) {
      setNewMessage(lastMessageRef.current);
      lastMessageRef.current = '';
      return;
    }

    // Fall back to the state if ref is empty
    if (lastSubmittedMessage) {
      setNewMessage(lastSubmittedMessage);
      // Clear the stored message after restoring
      setLastSubmittedMessage('');
    }
  }, [lastSubmittedMessage, setNewMessage, setLastSubmittedMessage]);

  // Combined error handler that both cleans up the failed message and recovers the text
  const handleProcessingError = useCallback(() => {
    // Important: Call recoverFromProcessingError first to restore the message
    // before sendFailCleanup removes the optimistic message
    recoverFromProcessingError();
    sendFailCleanup();
  }, [recoverFromProcessingError, sendFailCleanup]);

  // Handle recovery from error - agent continued processing after error cleanup was done
  // This reverses the error cleanup by putting the message back in chat
  const handleRecoverFromError = useCallback(() => {
    console.log(
      'ThreadView: Recovering from error - restoring message to chat',
    );
    // If there's content in the textarea (from error recovery), put it back in chat
    if (newMessage) {
      const restoredMessage: AgentMessage = {
        role: 'user',
        content: newMessage,
        timestamp: Date.now(),
      };
      setMessages((prevMessages) => [...prevMessages, restoredMessage]);
      setNewMessage(''); // Clear the textarea
    }
  }, [newMessage, setMessages, setNewMessage]);

  // Track the most recent message timestamp when pagination starts
  const lastMessageTimestampRef = useRef<number | null>(null);

  // Function to handle loading more messages
  const handleLoadMoreMessages = useCallback(async () => {
    if (!threadId || !loadMoreMessages || paginationState.loading) return;

    // Lock scrolling FIRST, before any async operations
    scrollLockedRef.current = true;
    console.log('Scroll locked before loading messages');

    // Store the timestamp of the most recent message before loading
    const mostRecentMessage = messages[messages.length - 1];
    const timestamp = mostRecentMessage?.timestamp;
    lastMessageTimestampRef.current =
      typeof timestamp === 'number'
        ? timestamp
        : typeof timestamp === 'string'
          ? parseInt(timestamp, 10)
          : Date.now();

    try {
      const currentMessageCount = messages.length;
      await loadMoreMessages(threadId, currentMessageCount, 20);
    } catch (error) {
      console.error('Error loading more messages:', error);
      scrollLockedRef.current = false;
    }
  }, [threadId, loadMoreMessages, paginationState.loading, messages]);

  // Track message count changes when scroll is locked to count new messages
  const previousMessageCountRef = useRef(messages.length);

  useEffect(() => {
    if (
      scrollLockedRef.current &&
      messages.length > previousMessageCountRef.current &&
      lastMessageTimestampRef.current !== null
    ) {
      // Count only messages with timestamps newer than when pagination started
      const newMessages = messages.filter((message) => {
        if (!message.timestamp || !lastMessageTimestampRef.current)
          return false;
        const messageTime =
          typeof message.timestamp === 'number'
            ? message.timestamp
            : typeof message.timestamp === 'string'
              ? parseInt(message.timestamp, 10)
              : 0;

        return messageTime > lastMessageTimestampRef.current;
      });
      const newMessageCount = newMessages.length;

      if (newMessageCount > 0) {
        setNewMessagesCount((prev) => prev + newMessageCount);
      }
    }
    previousMessageCountRef.current = messages.length;
  }, [messages.length]);

  const handleSendMessage = async (
    message: string,
    mentionsOverride?: MentionedUser[],
    options?: {
      createMandate?: boolean;
      mandateItems?: {
        actionLine: string;
        descriptionLine: string;
        price?: number;
        price_currency?: string;
      }[];
    },
  ) => {
    // Prepend reply context if replying to something
    let finalMessage = message;
    const currentReplyTo = getReplyTo();
    if (currentReplyTo && currentReplyTo.length > 0) {
      // Routine-creation items use their extraContext as a standalone context prefix
      const routineItem = currentReplyTo.find(
        (item) => item.id === 'routine-creation',
      );
      if (routineItem?.extraContext) {
        finalMessage = `FRONTEND_CONTEXT[${routineItem.extraContext}] ${message}`;
      } else {
        const replyParts = currentReplyTo.map((item) => {
          const contextPart = item.extraContext
            ? ` Context: ${item.extraContext}.`
            : '';
          return `${item.actionLine}: ${item.descriptionLine}.${contextPart}`;
        });
        finalMessage = `[* ${replyParts.join(' | ')} *] ${message}`;
      }
    }

    // Get successfully uploaded files
    const successfulUploads = uploadedFiles.filter(
      (file) => file.status === 'success',
    );
    // Store the message for potential recovery if processing fails
    setLastSubmittedMessage(finalMessage);
    lastMessageRef.current = finalMessage;

    // Clear uploaded files after sending
    setUploadedFiles([]);

    // Toggle the reset error flag to clear any error state in StreamHandler
    setResetStreamHandlerErrorFlag((prev: boolean) => !prev);

    // Create optimistic message with multi-modal content (show original message to user, not the prefixed one)
    const messageContent = createMessageContent(message, successfulUploads);

    const optimisticMessage: AgentMessage = {
      role: 'user',
      content: messageContent,
    };

    // Add optimistic message to parent's state
    setMessages([...messages, optimisticMessage]);

    // Use override if provided (from grant access flow), otherwise capture current state
    const mentionsToSend = mentionsOverride
      ? [...mentionsOverride]
      : [...mentions];

    // Clear input field, mentions, and reply-to immediately
    setNewMessage('');
    setMentions([]);
    clearReplyTo();

    // Resize the textarea after clearing
    setTimeout(() => {
      if (textareaRef.current) {
        adjustTextareaHeight(textareaRef.current);
      }
    }, 0);

    try {
      setLoading(true); // Will use threadId or NEW_THREAD key

      // Ensure Pusher connection is active before sending the message
      // This will reconnect if needed and wait for connection to establish
      console.log('Ensuring Pusher connection before sending message...');
      await ensureConnection();

      const token = await getAccessTokenSilently();

      // Extract image and file URLs from successful uploads
      const { imageUrls, fileUrls } = extractFileUrls(successfulUploads);

      // Get current form values for collaborative editing
      const formData = threadId ? getFormValues(threadId) : null;

      // Get the current AI reporting tree from ChatCanvas
      const tree = chatCanvasRef.current?.getTree() || [];

      // Build mentions array for request
      const mentionsForRequest =
        mentionsToSend.length > 0
          ? mentionsToSend.map((m) => ({
              userId: m.userId,
              displayName: m.displayName,
            }))
          : undefined;

      // Determine creation_type from ReplyTo context (e.g. routine-creation)
      const isRoutineCreation = currentReplyTo.some(
        (item) => item.id === 'routine-creation',
      );

      const requestBody: AgentRequestBody = {
        message: finalMessage,
        ...(threadId && { thread_id: threadId }),
        ...(agentId && { agent_id: agentId }),
        ...(imageUrls.length > 0 && { image_urls: imageUrls }),
        ...(fileUrls.length > 0 && { file_urls: fileUrls }),
        ...(formData?.values && { form_values: formData.values }),
        ...(formData?.id && { form_id: formData.id }),
        ...(tree.length > 0 && { tree }),
        ...(discussingObject && { discussing_object: discussingObject }),
        ...(discussingObjectMatch && {
          discussing_object_match: discussingObjectMatch,
        }),
        ...(mentionsForRequest && { mentions: mentionsForRequest }),
        ...(options?.createMandate && { create_mandate: true }),
        ...(options?.mandateItems && { mandate_items: options.mandateItems }),
        ...(!threadId &&
          isRoutineCreation && { creation_type: 'autopilot' as const }),
      };

      let response: AxiosResponse<AgentMessageResponse>;
      try {
        response = await api.post<AgentMessageResponse>(
          '/agents',
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Store the thread ID for potential abort
        if (response.data.thread_id) {
          setThreadIdForAbort(response.data.thread_id);
        }
      } catch (error) {
        // Network error or other exception
        // Revert optimistic update
        sendFailCleanup();

        setLoading(false);

        if (onCreateError) {
          onCreateError(
            MessageSendErrorType.POSSIBLE_NETWORK_ERROR,
            error instanceof Error ? error.message : 'Network error occurred',
            message,
          );
        }
        return;
      }

      if (response.data.success) {
        // If streaming is enabled, we'll get updates via Pusher
        // The loading state will be cleared when we receive the agent-complete event
        if (!response.data.streaming) {
          // If not streaming, handle the response as before
          setLoading(false);

          // Get the agent's response messages
          const responseMessages = response.data.messages || [];

          // Only handle existing threads here
          // For new threads with streaming, StreamHandler will handle it
          if (threadId) {
            // If continuing an existing conversation, notify parent with response messages
            onMessageSent(threadId, responseMessages);
          }
        }

        // Only call onMessageSent for new threads if streaming is NOT enabled
        // When streaming is enabled, StreamHandler will handle this
        if (!threadId && response.data.thread_id) {
          const newId = response.data.thread_id;
          setNewThreadId(newId);
          setThreadLoading(newId, true);
        }
      } else {
        // Revert optimistic update on error by removing the last message
        sendFailCleanup();

        setLoading(false);

        if (onCreateError) {
          onCreateError(
            MessageSendErrorType.UNSUCCESSFUL,
            response.data.error || 'Failed to send message',
            message,
          );
        }
      }
    } catch (error) {
      // Revert optimistic update
      sendFailCleanup();

      setLoading(false);

      console.error('Error sending message:', error);
      if (onCreateError) {
        onCreateError(
          MessageSendErrorType.UNKNOWN,
          error instanceof Error ? error.message : 'An error occurred',
          message,
        );
      }
    }
  };

  // Effect to handle recovery message when it changes
  useEffect(() => {
    // Only process the recovery message if it exists
    if (recoveryMessage) {
      setNewMessage(recoveryMessage);

      // Use a timeout to avoid potential re-render issues
      // This ensures the state update happens after the current render cycle
      setTimeout(() => {
        if (onRecoveryMessageUsed) {
          onRecoveryMessageUsed();
        }
      }, 0);
    }
  }, [recoveryMessage]);

  // Register and unregister agent error callback
  useEffect(() => {
    const callbackId = 'thread-view-agent-error';

    // Register the callback
    registerAgentErrorCallback(callbackId, handleAgentError);

    // Clean up on unmount
    return () => {
      unregisterAgentErrorCallback(callbackId);
    };
  }, [
    registerAgentErrorCallback,
    unregisterAgentErrorCallback,
    handleAgentError,
  ]);

  // Register WebRTC call requested callback
  useEffect(() => {
    if (!threadIdSafe || threadIdSafe === NEW_THREAD) return;

    const callbackId = `webrtc-call-${threadIdSafe}`;

    const handleCallRequested = (
      eventThreadId: string,
      data: WebRTCCallRequestedEvent,
    ) => {
      if (eventThreadId === threadIdSafe) {
        setCallPromptMessage(data.message || null);
        setCallPromptAgentName(data.agentName || null);
        setCallPromptStructuredCallId(data.structuredCallId || null);
        setShowCallPrompt(true);
      }
    };

    registerWebRTCCallRequestedCallback(callbackId, handleCallRequested);

    return () => {
      unregisterWebRTCCallRequestedCallback(callbackId);
    };
  }, [
    threadIdSafe,
    registerWebRTCCallRequestedCallback,
    unregisterWebRTCCallRequestedCallback,
  ]);

  // Gate between submit flow and handleSendMessage — checks for spending intent
  const proceedToSend = async (
    message: string,
    mentionsOverride?: MentionedUser[],
  ) => {
    const currentReplyTo = getReplyTo();
    const bookableItems = currentReplyTo.filter((item) => item.bookable);

    if (bookableItems.length > 0) {
      // Capture and clear input immediately so UI feels responsive
      const savedMentions = mentionsOverride
        ? [...mentionsOverride]
        : [...mentions];
      setNewMessage('');
      setMentions([]);
      setCheckingSpendingIntent(true);

      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.post(
          '/agents/check-spending-intent',
          {
            message,
            items: bookableItems.map((i) => ({
              actionLine: i.actionLine,
              descriptionLine: i.descriptionLine,
            })),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setCheckingSpendingIntent(false);

        if (data.spendingIntent) {
          setPendingSpendMessage(message);
          setPendingSpendMentions(savedMentions);
          setShowSpendingApprovalModal(true);
          return;
        }
      } catch (error) {
        console.error('Error checking spending intent:', error);
        setCheckingSpendingIntent(false);
        // On error, proceed without blocking
      }

      await handleSendMessage(
        message,
        savedMentions.length > 0 ? savedMentions : mentionsOverride,
      );
      return;
    }

    await handleSendMessage(message, mentionsOverride);
  };

  const handleApproveSpending = async () => {
    if (!pendingSpendMessage) return;
    setShowSpendingApprovalModal(false);
    const msg = pendingSpendMessage;
    const mns = pendingSpendMentions;
    setPendingSpendMessage(null);
    setPendingSpendMentions([]);
    const bookableItems = getReplyTo().filter((item) => item.bookable);
    await handleSendMessage(msg, mns.length > 0 ? mns : undefined, {
      createMandate: true,
      mandateItems: bookableItems.map((item) => ({
        actionLine: item.actionLine,
        descriptionLine: item.descriptionLine,
        price: item.price,
        price_currency: item.price_currency,
        isDeposit: item.isDeposit,
      })),
    });
  };

  const handleCancelSpending = () => {
    setShowSpendingApprovalModal(false);
    // Restore message and mentions to the textarea — don't send
    if (pendingSpendMessage) {
      setNewMessage(pendingSpendMessage);
    }
    if (pendingSpendMentions.length > 0) {
      setMentions(pendingSpendMentions);
    }
    setPendingSpendMessage(null);
    setPendingSpendMentions([]);
    setCheckingSpendingIntent(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Allow sending if there's a message or if there are successfully uploaded files
    // For main threads: allow sending while loading (backend will auto-abort and run new message)
    // For sub-threads: block sending while loading (preserve old behavior)
    const hasSuccessfulUploads = uploadedFiles.some(
      (file) => file.status === 'success',
    );
    if (!newMessage.trim() && !hasSuccessfulUploads) return;
    if (isSubThread && loading) return;

    // Check if any mentioned users need access (don't have WRITE, ADMIN, or OWNER)
    const needAccess = mentions.filter((m) => {
      const perm = m.currentPermission;
      return !perm || perm === 'NONE' || perm === 'READ';
    });

    if (needAccess.length > 0 && threadId) {
      // Show confirmation modal - save ALL mentions, not just those needing access
      setUsersNeedingAccess(needAccess);
      setPendingMessage(newMessage);
      setPendingMentions([...mentions]); // Save all mentions for sending
      setShowMentionAccessModal(true);
      return;
    }

    try {
      await proceedToSend(extractPlainText(newMessage));
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      if (onCreateError) {
        onCreateError(
          MessageSendErrorType.UNKNOWN,
          error instanceof Error ? error.message : 'An error occurred',
        );
      }
    }
  };

  // Grant access to mentioned users and send the message
  const handleGrantAccessAndSend = async () => {
    if (!threadId || !pendingMessage) return;

    setLoading(true);
    setShowMentionAccessModal(false);

    try {
      const token = await getAccessTokenSilently();

      // Grant write access to users needing it
      const shares = usersNeedingAccess.map((user) => ({
        user_id: user.userId,
        permission_level: 'WRITE',
      }));

      await api.put(
        `/agents/threads/${threadId}/shares`,
        { shares },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Send the message after granting access, passing mentions directly
      await proceedToSend(extractPlainText(pendingMessage), pendingMentions);
      setPendingMessage(null);
      setPendingMentions([]);
      setUsersNeedingAccess([]);
    } catch (error) {
      console.error('Error granting access or sending:', error);
      // Recover the message and ALL mentions so user can try again
      setNewMessage(pendingMessage);
      setMentions(pendingMentions);
      setPendingMessage(null);
      setPendingMentions([]);
      setUsersNeedingAccess([]);
      setLoading(false);
      if (onCreateError) {
        onCreateError(
          MessageSendErrorType.UNKNOWN,
          error instanceof Error ? error.message : 'Failed to grant access',
        );
      }
    }
  };

  // Handle textarea height adjustment
  const adjustTextareaHeight = (element: HTMLTextAreaElement) => {
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
    element.style.minHeight = 'auto';
  };

  // Handle input change (for MentionTextarea)
  const handleMentionChange = useCallback(
    (value: string, newMentions: MentionedUser[]) => {
      setNewMessage(value);
      setMentions(newMentions);
    },
    [],
  );

  // Extract plain text from mention markup for sending
  const extractPlainText = (value: string): string => {
    // Convert @[Name](userId) to @Name
    return value.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1');
  };

  // Handle key press in textarea
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // If Enter is pressed without Shift, submit the form
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      // Allow sending if there's a message or if there are successfully uploaded files
      // For main threads: allow sending while loading (backend will auto-abort and run new message)
      // For sub-threads: block sending while loading (preserve old behavior)
      const hasSuccessfulUploads = uploadedFiles.some(
        (file) => file.status === 'success',
      );
      const canSend = newMessage.trim() || hasSuccessfulUploads;
      const blockedBySubThread = isSubThread && loading;
      if (canSend && !blockedBySubThread) {
        handleSubmit(e as unknown as FormEvent);
      }
    }
  };

  // Escape key handler — hang up call when in audio mode
  useEffect(() => {
    if (!isAudioMode) return;
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShouldHangupCall(true);
      }
    };
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [isAudioMode]);

  // Handle file removal
  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((file) => file.id !== fileId),
    );
  };

  // Handle toggle recording
  const toggleRecording = async () => {
    if (isRecording) {
      // Stop recording
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      }
    } else {
      // Start recording
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/webm',
        });

        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = async () => {
          // Stop all tracks
          stream.getTracks().forEach((track) => track.stop());

          // Process the recording
          const audioBlob = new Blob(audioChunksRef.current, {
            type: 'audio/webm',
          });
          await transcribeAudio(audioBlob);
        };

        mediaRecorder.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Error starting recording:', error);
        alert('Could not access microphone. Please check your permissions.');
      }
    }
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    setIsTranscribing(true);

    try {
      const token = await getAccessTokenSilently();
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await api.post('/voice-transcribe', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success && response.data.transcription) {
        // Append transcription to existing message (allows recording in parts)
        setNewMessage((prevMessage) => {
          const separator = prevMessage.trim() ? ' ' : '';
          return prevMessage + separator + response.data.transcription;
        });

        // Resize the textarea and auto-focus
        setTimeout(() => {
          if (textareaRef.current) {
            adjustTextareaHeight(textareaRef.current);
            textareaRef.current.focus();
          }
        }, 0);
      } else {
        console.error('Transcription failed:', response.data);
        alert('Failed to transcribe audio. Please try again.');
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
      alert('Error transcribing audio. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleDeclineCall = async () => {
    setShowCallPrompt(false);

    // Notify other components (like CallNotification) that the call was dismissed
    if (threadIdSafe) {
      dismissWebRTCCall(threadIdSafe);
    }

    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/webrtc/decline-call',
        {
          thread_id: threadIdSafe,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } catch (error) {
      console.error('Error declining call:', error);
      // Silent fail - user already dismissed the prompt
    }
  };

  return (
    <div
      className={`thread-view${inMiniMode ? ' mini-view' : ''} ${discussingObject === 'fast-chat' ? 'fast-chat' : ''}`}
    >
      {/* Hidden preload for agent avatar - keeps decoded image in memory */}
      {agent?.image_url && (
        <img
          src={agent.image_url}
          alt=''
          style={{ display: 'none' }}
          aria-hidden='true'
        />
      )}
      <div
        className={`thread-messages${isAudioMode ? ' voice-active' : ''}`}
        onScroll={handleScroll}
      >
        <div
          className={`message-list${messages.length === 0 ? ' message-list-empty' : ''}`}
        >
          {messages.length === 0 && discussingObject === 'fast-chat' && (
            <div className='fast-chat-empty-state'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='32'
                height='32'
                viewBox='0 0 32 32'
                fill='none'
              >
                <path
                  d='M17.3332 2.66663L5.45784 16.9171C4.99276 17.4752 4.76022 17.7542 4.75667 17.9899C4.75358 18.1948 4.84487 18.3897 5.00424 18.5185C5.18756 18.6666 5.5508 18.6666 6.27728 18.6666H15.9999L14.6666 29.3333L26.542 15.0828C27.007 14.5247 27.2396 14.4014 27.2431 14.01C27.2462 13.8051 27.1549 13.6102 26.9956 13.4814C26.8122 13.3333 26.449 13.3333 25.7225 13.3333H15.9999L17.3332 2.66663Z'
                  stroke='#838B9B'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <p className='leader'>
                Use quick chat to get answers, check spelling, or just find out
                the weather.
              </p>
              <p className='follower'>
                Your quick chat doesn't connect to your Dreamies.
              </p>
            </div>
          )}
          {/* Load more button */}
          {paginationState.hasMore && threadId && loadMoreMessages && (
            // ...
            <div className='load-more-container'>
              <button
                className={`load-more-button ${paginationState.loading ? 'loading' : ''}`}
                onClick={handleLoadMoreMessages}
                disabled={paginationState.loading}
              >
                {paginationState.loading ? (
                  <>
                    Loading
                    <LoadingRingsIcon />
                  </>
                ) : (
                  'Load more messages'
                )}
              </button>
            </div>
          )}
          {(() => {
            // Group and filter messages
            const groupedAndFilteredMessages = groupToolMessages(
              messages.filter((message) => {
                // Filter out messages hidden from user
                if (message.metadata?.hiddenFromUser) return false;

                // Filter out tool calls unless im=true is set.
                // Exception: showResponseArtifact always passes through
                // so the inline artifact card renders for users.
                if (message.role === 'tool') {
                  const isArtifactTool =
                    typeof message.content === 'string' &&
                    message.content === 'showResponseArtifact' &&
                    !!message.tool_call_input?.artifactId;
                  if (!isArtifactTool && !showInternalMessages) return false;
                  const hiddenToolFamilies = ['acknowledgeInactivity'];
                  if (
                    typeof message.content === 'string' &&
                    hiddenToolFamilies.includes(message.content)
                  ) {
                    return false;
                  }
                }

                // Filter out system messages (messages that start with
                // system prefixes). Handles both string content and
                // multimodal arrays — without the array case, a
                // SYSTEM_MESSAGE that also has an image or file attached
                // would leak into the visible chat.
                if (message.role === 'user' || message.role === 'agent') {
                  if (showInternalMessages) return true;
                  if (isInternalSystemMessage(message.content)) return false;
                }

                return true;
              }),
            );

            // Track if we've already shown the "New replies" divider
            let dividerShown = false;
            // Track the last date we showed a divider for
            let lastDividerDate: number | null = null;
            // Track last message times for user and agent messages separately
            let lastUserMessageTime: number | null = null;
            let lastAgentMessageTime: number | null = null;

            // Find the index of the last user message for scroll targeting
            const lastUserMessageIndex = groupedAndFilteredMessages.reduce(
              (lastIdx, item, idx) => {
                if (!('tools' in item && 'family' in item)) {
                  const message = item as AgentMessage;
                  if (message.role === 'user') {
                    return idx;
                  }
                }
                return lastIdx;
              },
              -1,
            );

            // Index of the agent's first visible message — where the
            // "Start Call to Discuss the Project" kickoff button is rendered
            // for agent-led project threads.
            const firstAgentMessageIndex = groupedAndFilteredMessages.findIndex(
              (item) =>
                !('tools' in item && 'family' in item) &&
                (item as AgentMessage).role === 'agent',
            );

            return groupedAndFilteredMessages.map((item, index, array) => {
              // Check if this is a tool group or a regular message
              const isActive = index === array.length - 1;

              // Check if we should show the "New replies" divider before this item
              const shouldShowDivider = (() => {
                // If we've already shown the divider, don't show again
                if (dividerShown) {
                  return false;
                }

                if (!lastReadAtForDivider) {
                  return false;
                }
                // Only skip the very first message (can't show divider before it)
                if (index === 0) {
                  console.log(
                    `Skipping divider: index ${index} (first message)`,
                  );
                  return false;
                }

                const lastReadAtTime = new Date(lastReadAtForDivider).getTime();

                // Get timestamp of current item
                let currentItemTime: number;
                if ('tools' in item && 'family' in item) {
                  const toolGroup = item as ToolGroup;
                  currentItemTime = toolGroup.tools[0]?.timestamp || 0;
                } else {
                  const message = item as AgentMessage;
                  currentItemTime = message.timestamp || 0;
                }

                // Get timestamp of previous item
                const prevItem = array[index - 1];
                let prevItemTime: number;
                if ('tools' in prevItem && 'family' in prevItem) {
                  const toolGroup = prevItem as ToolGroup;
                  prevItemTime = toolGroup.tools[0]?.timestamp || 0;
                } else {
                  const message = prevItem as AgentMessage;
                  prevItemTime = message.timestamp || 0;
                }

                const result =
                  prevItemTime <= lastReadAtTime &&
                  currentItemTime > lastReadAtTime;

                console.log('Divider check:', {
                  index,
                  lastReadAtTime,
                  prevItemTime,
                  currentItemTime,
                  result,
                });

                // Mark as shown if we're going to show it
                if (result) {
                  dividerShown = true;
                }

                return result;
              })();

              // Check if we should show a date divider before this item
              const shouldShowDateDivider = (() => {
                // Get timestamp of current item
                let currentItemTime: number;
                if ('tools' in item && 'family' in item) {
                  const toolGroup = item as ToolGroup;
                  currentItemTime = toolGroup.tools[0]?.timestamp || 0;
                } else {
                  const message = item as AgentMessage;
                  currentItemTime = message.timestamp || 0;
                }

                // Skip if no valid timestamp
                if (!currentItemTime) return false;

                // Show date divider for first message unless it's today
                if (index === 0) {
                  lastDividerDate = currentItemTime;
                  const today = new Date();
                  const messageDate = new Date(currentItemTime);
                  const isToday =
                    messageDate.getDate() === today.getDate() &&
                    messageDate.getMonth() === today.getMonth() &&
                    messageDate.getFullYear() === today.getFullYear();
                  return !isToday;
                }

                // Check if we need a new date divider
                if (
                  lastDividerDate &&
                  isDifferentDay(lastDividerDate, currentItemTime)
                ) {
                  lastDividerDate = currentItemTime;
                  return true;
                }

                return false;
              })();

              const dateDividerText = (() => {
                let currentItemTime: number;
                if ('tools' in item && 'family' in item) {
                  const toolGroup = item as ToolGroup;
                  currentItemTime = toolGroup.tools[0]?.timestamp || 0;
                } else {
                  const message = item as AgentMessage;
                  currentItemTime = message.timestamp || 0;
                }
                return currentItemTime
                  ? formatDateDivider(currentItemTime)
                  : '';
              })();

              if ('tools' in item && 'family' in item) {
                // It's a tool group
                const toolGroup = item as ToolGroup;

                // A tool group is only considered active if it's the very last message
                // This indicates it's still in progress

                // Separate showResponseArtifact tool calls from the group —
                // these render as inline artifact cards, not collapsed tool lines.
                const artifactTools = toolGroup.tools.filter(
                  (t) =>
                    typeof t.content === 'string' &&
                    t.content === 'showResponseArtifact' &&
                    t.tool_call_input?.artifactId,
                );
                const remainingTools = toolGroup.tools.filter(
                  (t) =>
                    !(
                      typeof t.content === 'string' &&
                      t.content === 'showResponseArtifact' &&
                      t.tool_call_input?.artifactId
                    ),
                );

                return (
                  <React.Fragment
                    key={`group-${index}-${toolGroup.tools[0]?.tool_call_id || toolGroup.tools[0]?.timestamp || ''}`}
                  >
                    {shouldShowDateDivider && (
                      <div className='date-divider'>
                        <span>{dateDividerText}</span>
                      </div>
                    )}
                    {shouldShowDivider && (
                      <div className='new-replies-divider'>
                        <span>New replies to this chat</span>
                      </div>
                    )}
                    {remainingTools.length > 0 && (
                      <div className='message tool-message'>
                        <ChatToolGroupDisplay
                          toolGroup={{ ...toolGroup, tools: remainingTools }}
                          isActive={isActive}
                          colors={
                            predominantColour
                              ? generateToolMessageColors(predominantColour)
                              : undefined
                          }
                        />
                      </div>
                    )}
                    {artifactTools.map((t) => (
                      <MessageOuterWrapper
                        key={`artifact-${t.tool_call_input!.artifactId}`}
                        messageRole='agent'
                        inMiniMode={inMiniMode}
                        agent={agent}
                        colorVariants={colorVariants}
                        discussingObject={discussingObject}
                      >
                        <InlineResponseArtifact
                          artifactId={t.tool_call_input!.artifactId}
                          threadId={threadId || ''}
                        />
                      </MessageOuterWrapper>
                    ))}
                  </React.Fragment>
                );
              } else {
                // It's a regular message
                const message = item as AgentMessage;

                // Check if we should show timestamp for user or agent messages
                const shouldShowTimestamp = (() => {
                  // Don't show for tool messages
                  if (message.role === 'tool') return false;

                  const currentTime = message.timestamp || 0;
                  if (!currentTime) return false;

                  // Check if this is the last message in a sequence of the same type
                  // For agent messages, skip over tool groups to find the next non-tool item
                  let nextNonToolItem = null;
                  let lookAheadIndex = index + 1;

                  while (lookAheadIndex < array.length) {
                    const item = array[lookAheadIndex];
                    if ('tools' in item && 'family' in item) {
                      // Skip tool groups for agent messages
                      if (message.role === 'agent') {
                        lookAheadIndex++;
                        continue;
                      }
                    }
                    nextNonToolItem = item;
                    break;
                  }

                  let isLastInSequence = false;

                  if (!nextNonToolItem) {
                    // This is the last message in the sequence (no more non-tool items)
                    isLastInSequence = true;
                  } else if (
                    'tools' in nextNonToolItem &&
                    'family' in nextNonToolItem
                  ) {
                    // Next non-tool item is a tool (only possible for user messages)
                    isLastInSequence = true;
                  } else {
                    // Next item is a message, check if it's a different role
                    const nextMessage = nextNonToolItem as AgentMessage;
                    isLastInSequence = nextMessage.role !== message.role;
                  }

                  const THREE_MINUTES_MS = 3 * 60 * 1000;

                  if (message.role === 'user') {
                    // Show if: last in sequence OR 3+ min gap from last shown timestamp
                    const hasTimeGap =
                      lastUserMessageTime !== null &&
                      currentTime - lastUserMessageTime >= THREE_MINUTES_MS;

                    if (isLastInSequence || hasTimeGap) {
                      lastUserMessageTime = currentTime;
                      return true;
                    }
                    return false;
                  } else if (message.role === 'agent') {
                    // Show if: last in sequence OR 3+ min gap from last shown timestamp
                    const hasTimeGap =
                      lastAgentMessageTime !== null &&
                      currentTime - lastAgentMessageTime >= THREE_MINUTES_MS;

                    if (isLastInSequence || hasTimeGap) {
                      lastAgentMessageTime = currentTime;
                      return true;
                    }
                    return false;
                  }

                  return false;
                })();

                const messageTimeText = message.metadata?.editedAt
                  ? `Edited at ${formatMessageTime(new Date(message.metadata.editedAt).getTime())}`
                  : message.timestamp
                    ? formatMessageTime(message.timestamp)
                    : '';

                return (
                  <React.Fragment
                    key={`message-${index}-${message.timestamp || message.tool_call_id || ''}`}
                  >
                    {shouldShowDateDivider && (
                      <div className='date-divider'>
                        <span>{dateDividerText}</span>
                      </div>
                    )}
                    {shouldShowDivider && (
                      <div className='new-replies-divider'>
                        <span>New replies to this chat</span>
                      </div>
                    )}
                    <MessageOuterWrapper
                      messageRole={message.role}
                      inMiniMode={inMiniMode}
                      agent={agent}
                      colorVariants={colorVariants}
                      discussingObject={discussingObject}
                      isSharedUserMessage={
                        message.role === 'user' &&
                        message.metadata?.userId &&
                        message.metadata.userId !== currentUserId
                      }
                    >
                      <div
                        className={`message ${
                          message.role === 'user'
                            ? message.metadata?.userId &&
                              message.metadata.userId !== currentUserId
                              ? 'user-message shared-user-message'
                              : 'user-message'
                            : message.role === 'tool'
                              ? 'tool-message'
                              : 'agent-message'
                        }${obscured && message.metadata?.obscured ? ' obscured' : ''}`}
                        onClick={
                          obscured && message.metadata?.obscured
                            ? () => handleDecipherMessage(message)
                            : undefined
                        }
                        style={
                          obscured && message.metadata?.obscured
                            ? { cursor: 'pointer' }
                            : undefined
                        }
                        title={
                          obscured && message.metadata?.obscured
                            ? 'Click to reveal'
                            : undefined
                        }
                      >
                        {message.role === 'tool' &&
                        typeof message.content === 'string' &&
                        message.content === 'showResponseArtifact' &&
                        message.tool_call_input?.artifactId ? (
                          <InlineResponseArtifact
                            artifactId={message.tool_call_input.artifactId}
                            threadId={threadId || ''}
                          />
                        ) : message.role === 'tool' ? (
                          <ChatToolGroupDisplay
                            toolGroup={{ family: '', tools: [message] }}
                            isActive={isActive}
                            colors={
                              predominantColour
                                ? generateToolMessageColors(predominantColour)
                                : undefined
                            }
                          />
                        ) : (
                          <>
                            {message.role === 'user' &&
                              message.metadata?.userName &&
                              message.metadata.userId !== currentUserId && (
                                <div className='message-author'>
                                  {message.metadata.userName}
                                </div>
                              )}
                            {/* Reply indicator for user messages */}
                            {message.role === 'user' && (
                              <MessageReplyIndicator
                                messageContent={message.content}
                                colorVariants={colorVariants}
                              />
                            )}
                            <div className='message-content'>
                              <MultiModalContent
                                content={message.content}
                                metadata={message.metadata}
                                threadId={threadId || undefined}
                                colors={
                                  message.role !== 'user' &&
                                  predominantColour &&
                                  colorVariants
                                    ? {
                                        iconBackground: colorVariants.darker,
                                      }
                                    : undefined
                                }
                              />
                            </div>
                          </>
                        )}
                      </div>
                      {shouldShowTimestamp && message.role !== 'tool' && (
                        <div className='message-timestamp'>
                          {messageTimeText}
                        </div>
                      )}
                      {/* Tap-to-start kickoff call: shown under the agent's
                          first message in agent-led project threads, replacing
                          the old auto-fired call prompt. */}
                      {voiceCallingEnabled &&
                        isAgentLedProject &&
                        index === firstAgentMessageIndex &&
                        !isAudioMode &&
                        !showCallPrompt &&
                        hasWritePermission && (
                          <button
                            type='button'
                            className='start-project-call-button'
                            onClick={() => {
                              setShowCallPrompt(false);
                              setIsAudioMode(true);
                            }}
                          >
                            <StartVoiceChatIcon />
                            <span>Start Call to Discuss the Project</span>
                          </button>
                        )}
                      {/* TODO: Sub-thread links temporarily disabled to reduce re-renders */}
                      {/* {message.role === 'user' && (
                            <SubThreadLinks
                              messageContent={
                                typeof message.content === 'string'
                                  ? message.content
                                  : undefined
                              }
                            />
                          )} */}
                      {/* TODO: Sub-thread consideration indicator temporarily disabled */}
                      {/* {message.role === 'user' && isActive && (
                            <SubThreadConsiderationIndicator />
                          )} */}
                    </MessageOuterWrapper>
                  </React.Fragment>
                );
              }
            });
          })()}

          <StreamHandler
            threadId={threadIdSafe}
            messages={messages}
            setMessages={setMessages}
            onMessageSent={memoizedOnMessageSent}
            onError={handleProcessingError}
            onRecoverFromError={handleRecoverFromError}
            onAbortSucceeded={handleAbortSucceeded}
            predominantColour={predominantColour}
            onScrollNeeded={scrollToBottom}
            resetErrorOnNewMessage={resetStreamHandlerErrorFlag}
            fetchThreadMessages={fetchThreadMessages}
            agentNickname={agentNickname}
            agentImageUrl={agent?.image_url}
            onThinkingChange={setIsAgentThinking}
            messageWrapper={({ children }) => (
              <MessageOuterWrapper
                messageRole='agent'
                inMiniMode={inMiniMode}
                agent={agent}
                colorVariants={colorVariants}
                discussingObject={discussingObject}
              >
                {children}
              </MessageOuterWrapper>
            )}
          />

          <AsyncToolDisplay threadId={threadIdSafe} />

          <ThirdPartyMessageHandler
            threadId={threadIdSafe}
            setMessages={setMessages}
          />

          <div ref={messagesEndRef} />
        </div>

        {/* Fixed scroll to bottom button */}
        {scrollLockedRef.current && newMessagesCount > 0 && (
          <div className='scroll-to-bottom-container'>
            <button
              className='scroll-to-bottom-button'
              onClick={handleScrollToBottomClick}
            >
              <span className='new-messages-count'>{newMessagesCount}</span>
              <DownArrowIcon />
            </button>
          </div>
        )}
      </div>
      {/* Show "Continue conversation in full screen" link in mini mode when there are messages */}
      {inMiniMode &&
        messages.length > 0 &&
        discussingObject !== 'fast-chat' && (
          <div className='mini-view-fullscreen-link'>
            <a
              href={`/conversations?agent_id=${agentId}&thread_id=${threadId}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              Continue conversation in full screen
            </a>
          </div>
        )}
      <div className='bottom-stuck'>
        <ReplyToPreview colorVariants={colorVariants} />
        {/* ConfirmationNotifications removed - confirmations are now handled via artifacts in the To-Do List */}
        {/* Mobile file preview - above dragger wrapper */}
        {!readOnly &&
          !isArchivedThread &&
          !isAudioMode &&
          !effectiveShowCallPrompt && (
            <div className='mobile-file-preview'>
              <FilePreview
                files={uploadedFiles}
                onRemoveFile={handleRemoveFile}
              />
            </div>
          )}
        {readOnly ? (
          <div className='read-only-notice'>
            Viewing thread in read-only mode
          </div>
        ) : isArchivedThread ? (
          <div className='archived-thread-notice'>
            This thread has been archived
          </div>
        ) : (
          <div
            className={`chat-input-wrapper${isOutOfCredits ? ' out-of-credits-hover' : ''}`}
          >
            {isOutOfCredits && (
              <div className='out-of-credits-tooltip'>
                You've used all your AI time allowance. You won't be able to
                chat until you top up, or your allowance resets
                {resetsAt
                  ? ` on ${new Date(resetsAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long' })}`
                  : ''}
              </div>
            )}
            <FileUploadDropZone
              onFileUpload={handleFileUpload}
              disabled={!hasWritePermission || isAudioMode || isOutOfCredits}
              className='dragger-wrapper'
            >
              <form
                className={`message-input-form${isAudioMode || effectiveShowCallPrompt ? ' audio-mode' : ''}${isAudioConnected ? ' connected-audio' : ''}${isOutOfCredits ? ' out-of-credits' : ''}`}
                onSubmit={handleSubmit}
                style={
                  {
                    '--audio-level': outputLevel,
                  } as React.CSSProperties
                }
              >
                {effectiveShowCallPrompt ? (
                  <div className='webrtc-call-request-wrapper'>
                    <div className='call-request-topper'>
                      <CallingIcon />
                      <div className='call-request-content'>
                        <div className='agent-name'>
                          {callPromptAgentName || agentName || 'Agent'} wants to
                          talk
                        </div>
                        {callPromptMessage && (
                          <div className='call-message'>
                            {callPromptMessage}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='call-request-actions'>
                      <button
                        className='answer-call-button'
                        onClick={() => {
                          setIsAudioMode(true);
                          setShowCallPrompt(false);
                          // Notify other components that the call was accepted
                          if (threadIdSafe) {
                            acceptWebRTCCall(threadIdSafe);
                          }
                        }}
                      >
                        Answer
                      </button>
                      <button
                        className='dismiss-call-button'
                        onClick={handleDeclineCall}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                ) : !isAudioMode ? (
                  <MentionTextarea
                    value={newMessage}
                    onChange={handleMentionChange}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      hasWritePermission
                        ? isSubThread
                          ? 'Reply'
                          : 'Follow-up...'
                        : 'You do not have permission to send messages'
                    }
                    disabled={!hasWritePermission || isOutOfCredits}
                    threadId={threadId}
                  />
                ) : (
                  <WebRTCVoiceChat
                    agentId={agentId}
                    threadId={threadId || undefined}
                    structuredCallId={callPromptStructuredCallId || undefined}
                    diallerMode={true}
                    autoStart={true}
                    shouldHangup={shouldHangupCall}
                    agentFirstMessage={callPromptMessage || undefined}
                    onConnectionChange={(connected) =>
                      setIsAudioConnected(connected)
                    }
                    onAudioLevelChange={(level) => setAudioLevel(level)}
                    onOutputLevelChange={(level) => setOutputLevel(level)}
                    onError={(error) => console.error('WebRTC Error:', error)}
                    onHangupComplete={() => {
                      setShouldHangupCall(false);
                      setIsAudioMode(false);
                    }}
                  />
                )}

                {/* File preview area */}
                {!isAudioMode && !effectiveShowCallPrompt && (
                  <FilePreview
                    files={uploadedFiles}
                    onRemoveFile={handleRemoveFile}
                  />
                )}

                <div
                  className='actions-wrapper'
                  style={isAudioMode ? { display: 'none' } : undefined}
                >
                  {/* DirectFileUploader component */}
                  {!isAudioMode && !effectiveShowCallPrompt ? (
                    <DirectFileUploader
                      onFileUpload={handleFileUpload}
                      disabled={
                        loading || !hasWritePermission || isOutOfCredits
                      }
                    />
                  ) : (
                    <div></div>
                  )}
                  <div className='buttons-wrapper'>
                    {/* Start a call from a project thread (mirrors the
                        main-thread call entry point). Hidden on the main thread,
                        which has its own floating call button. */}
                    {voiceCallingEnabled &&
                      !isAudioMode &&
                      !effectiveShowCallPrompt &&
                      hasWritePermission &&
                      discussingObject === 'Project' && (
                        <div
                          className='start-call-button'
                          title='Start a call'
                          onClick={() => {
                            if (loading || isOutOfCredits) return;
                            setIsAudioMode(true);
                          }}
                          style={{
                            opacity: loading || isOutOfCredits ? 0.5 : 1,
                            cursor:
                              loading || isOutOfCredits
                                ? 'not-allowed'
                                : 'pointer',
                          }}
                        >
                          <StartVoiceChatIcon />
                        </div>
                      )}
                    {!isAudioMode && !effectiveShowCallPrompt && (
                      <>
                        <div
                          className={`microphone-button ${isRecording ? 'recording' : ''} ${isTranscribing ? 'transcribing' : ''}`}
                          onClick={() => {
                            if (
                              !hasWritePermission ||
                              loading ||
                              isTranscribing ||
                              isOutOfCredits
                            )
                              return;
                            toggleRecording();
                          }}
                          style={{
                            opacity:
                              !hasWritePermission ||
                              loading ||
                              isTranscribing ||
                              isOutOfCredits
                                ? 0.5
                                : 1,
                            cursor:
                              !hasWritePermission ||
                              loading ||
                              isTranscribing ||
                              isOutOfCredits
                                ? 'not-allowed'
                                : 'pointer',
                          }}
                        >
                          {isTranscribing ? (
                            <div className='loading-rings'>
                              <LoadingRingsIcon />
                            </div>
                          ) : (
                            <MicIcon />
                          )}
                        </div>
                      </>
                    )}
                    {!effectiveShowCallPrompt && (
                      <>
                        {!isAudioMode ? (
                          <button
                            className='send-button'
                            disabled={
                              // Disable submit when no message, but NOT when showing abort button
                              (!newMessage.trim() && !(loading && !aborting)) ||
                              !hasWritePermission
                            }
                            onClick={
                              loading &&
                              !aborting &&
                              (!newMessage.trim() || isSubThread)
                                ? handleAbortStreaming
                                : undefined
                            }
                            type={
                              loading &&
                              !aborting &&
                              (!newMessage.trim() || isSubThread)
                                ? 'button'
                                : 'submit'
                            }
                          >
                            {checkingSpendingIntent ? (
                              <div className='loading-rings'>
                                <LoadingRingsIcon />
                              </div>
                            ) : loading &&
                              (!newMessage.trim() || isSubThread) ? (
                              aborting ? (
                                <div className='loading-rings'>
                                  <LoadingRingsIcon />
                                </div>
                              ) : (
                                <div
                                  className='stop-square'
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    flexShrink: 0,
                                    aspectRatio: '1/1',
                                    borderRadius: '2px',
                                    background: 'var(--Plus-White, #FFFEFC)',
                                  }}
                                ></div>
                              )
                            ) : (
                              <div className='chat-send'>
                                <ChatSendIcon />
                              </div>
                            )}
                          </button>
                        ) : (
                          <button
                            className='send-button stop-audio-button'
                            onClick={() => {
                              setShouldHangupCall(true);
                            }}
                            type='button'
                          >
                            <div
                              className='stop-square'
                              style={{
                                width: '12px',
                                height: '12px',
                                flexShrink: 0,
                                aspectRatio: '1/1',
                                borderRadius: '2px',
                                background: 'var(--Plus-White, #FFFEFC)',
                              }}
                            ></div>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </form>
            </FileUploadDropZone>
          </div>
        )}
      </div>

      <MentionAccessModal
        show={showMentionAccessModal}
        onClose={() => {
          setShowMentionAccessModal(false);
          setPendingMessage(null);
          setUsersNeedingAccess([]);
        }}
        onConfirm={handleGrantAccessAndSend}
        usersNeedingAccess={usersNeedingAccess}
      />

      <SpendingApprovalModal
        show={showSpendingApprovalModal}
        onApprove={handleApproveSpending}
        onCancel={handleCancelSpending}
        items={getReplyTo()}
        agentImageUrl={agent?.image_url}
        assistantName={agent?.nickname || 'Assistant'}
        isDeposit={getReplyTo().some((item) => item.isDeposit)}
        merchantName={
          getReplyTo().find((item) => item.merchantName)?.merchantName
        }
      />
    </div>
  );
};

export default ThreadView;
